import React, { createContext, useContext } from 'react';
import { Analytics, Auth } from 'aws-amplify';
import { isBrowser } from 'react-device-detect';
import { logger } from '../utils/logger';

// Analytics Context Type 정의
interface AnalyticsContextType {
  recordEvent: (
    eventType: string, 
    success: boolean, 
    attributes?: Record<string, any>,
    metrics?: Record<string, number>
  ) => void;
  recordLoginEvent: (
    loginType: string, 
    success: boolean, 
    attributes?: Record<string, any>
  ) => void;
  recordOrderEvent: (
    orderType: string, 
    success: boolean, 
    items?: number,
    totalAmount?: number, 
    attributes?: Record<string, any>
  ) => void;
  // 페이지 조회는 PageTracker 컴포넌트로 이동되어 직접 호출되지 않음
  // recordPageView: (pageName: string, attributes?: Record<string, any>) => void;
}

// Context 생성
const AnalyticsContext = createContext<AnalyticsContextType | undefined>(undefined);

// Provider 컴포넌트
export const AnalyticsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // 기본 이벤트 기록 함수
  const recordEvent = async (
    eventType: string, 
    success: boolean, 
    attributes: Record<string, any> = {},
    metrics: Record<string, number> = {}
  ) => {
    try {
      // STS 자격 증명 발급
      await Auth.currentCredentials();
      
      // custId를 customer_id로 사용하거나 storeName 또는 storeId를 대체로 사용
      const storeId = attributes.customer_id || attributes.storeName || attributes.storeId || 'unknown_store';
      
      // 기본 속성 설정
      const attrs = {
        ...attributes,
        success: success.toString(),
        timestamp: new Date().toISOString(),
        platform: isBrowser ? 'web' : 'mobile',
        partitionKey: storeId, // custId를 파티션 키로 사용
        eventCategory: eventType.split('_')[0] || 'general' // 이벤트 카테고리 추가
      };
      
      // 콘솔에 로그 기록 (디버깅용)
      logger.debug(`Pinpoint 이벤트 기록 시도: ${eventType}`, attrs);
      
      try {
        // Amplify Analytics로 이벤트 기록 - AWS Amplify v5 방식
        Analytics.record({
          name: eventType,
          attributes: attrs,
          metrics: metrics
        });
        logger.debug(`✅ Pinpoint 이벤트 기록 전송 완료: ${eventType}`);
      } catch (recordError: any) {
        logger.error(`❌ Pinpoint 이벤트 기록 실패: ${eventType}`, recordError);
      }
    } catch (error: any) {
      logger.error('Pinpoint 이벤트 기록 중 오류 발생:', error);
    }
  };

  // 로그인 이벤트 기록 함수
  const recordLoginEvent = (
    loginType: string, 
    success: boolean, 
    attributes: Record<string, any> = {}
  ) => {
    // 로그인 타입에 따른 이벤트 이름 포맷팅
    const eventType = `login_${loginType}`;
    
    // 기본 메트릭 설정
    const metrics = {
      retryCount: attributes.retryCount || 0
    };
    
    recordEvent(eventType, success, attributes, metrics);
  };

  // 주문 이벤트 기록 함수
  const recordOrderEvent = (
    orderType: string,
    success: boolean,
    items: number = 0,
    totalAmount: number = 0,
    attributes: Record<string, any> = {}
  ) => {
    // 주문 타입에 따른 이벤트 이름 포맷팅
    const eventType = `order_${orderType}`;
    
    // 주문 관련 메트릭 설정
    const metrics = {
      items,
      totalAmount,
      retryCount: attributes.retryCount || 0
    };
    
    recordEvent(eventType, success, attributes, metrics);
  };

  // Context 값
  const contextValue: AnalyticsContextType = {
    recordEvent,
    recordLoginEvent,
    recordOrderEvent,
    // recordPageView 제거 (PageTracker 컴포넌트로 통합)
  };

  return (
    <AnalyticsContext.Provider value={contextValue}>
      {children}
    </AnalyticsContext.Provider>
  );
};

// Custom Hook
export const useAnalytics = (): AnalyticsContextType => {
  const context = useContext(AnalyticsContext);
  if (context === undefined) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }
  return context;
}; 